
import React from 'react';
import { TableCell } from '@/components/ui/table';
import { Calendar } from 'lucide-react';
import { Attendee } from '@/types/attendee';
import { formatTimeAgo, formatDateTime, formatContribution } from '../utils/formatUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import CheckInButton from '../CheckInButton';
import PaymentToggle from '../payment/PaymentToggle';
import AttendanceStatus from '../attendance/AttendanceStatus';
import { NameCell } from './cells/NameCell';
import { CodeCell } from './cells/CodeCell';
import { ClassCell } from './cells/ClassCell';
import { TShirtCell } from './cells/TShirtCell';

interface AttendeeRowCellsProps {
  attendee: Attendee;
  isPaid: boolean;
  isUpdating: boolean;
  localAttendance: boolean;
  localCheckedInAt?: string;
  onCheckInSuccess: (attendee: Attendee, timestamp: string) => void;
  onDirectUpdate: (code: string, updates: any) => Promise<any>;
  onSilentUpdate?: (attendee: Attendee) => void;
  handlePaymentToggleRequest: (checked: boolean) => void;
  showContribution: boolean;
}

export const AttendeeRowCells: React.FC<AttendeeRowCellsProps> = ({
  attendee,
  isPaid,
  isUpdating,
  localAttendance,
  localCheckedInAt,
  onCheckInSuccess,
  onDirectUpdate,
  onSilentUpdate,
  handlePaymentToggleRequest,
  showContribution
}) => {
  const isMobile = useIsMobile();
  
  // Log t-shirt size to help debug
  console.log(`AttendeeRowCells for ${attendee.code}: t_shirt_size = ${attendee.t_shirt_size}`);
  
  // Preserve the original attendee for reference
  const originalAttendee = attendee;
  
  // Create a modified version of onDirectUpdate that always preserves t_shirt_size
  const enhancedDirectUpdate = async (code: string, updates: any) => {
    // Always include the t_shirt_size in updates
    const enhancedUpdates = {
      ...updates,
      t_shirt_size: updates.t_shirt_size || attendee.t_shirt_size
    };
    
    console.log(`Enhanced direct update for ${code} with t_shirt_size:`, enhancedUpdates.t_shirt_size);
    
    return onDirectUpdate(code, enhancedUpdates);
  };
  
  // Add event listener for payment status updates
  React.useEffect(() => {
    const handlePaymentUpdate = (event: CustomEvent) => {
      if (event.detail?.attendeeCode === attendee.code) {
        console.log(`Received payment update event for ${attendee.code}`, event.detail);
        if (event.detail.updatedAttendee && onSilentUpdate) {
          onSilentUpdate(event.detail.updatedAttendee);
        }
      }
    };
    
    window.addEventListener('payment-status-updated', handlePaymentUpdate as EventListener);
    
    return () => {
      window.removeEventListener('payment-status-updated', handlePaymentUpdate as EventListener);
    };
  }, [attendee.code, onSilentUpdate]);
  
  return (
    <>
      <NameCell attendee={attendee} isMobile={isMobile} />
      <CodeCell attendee={attendee} isMobile={isMobile} />
      <ClassCell attendee={attendee} isMobile={isMobile} />
      <TShirtCell tShirtSize={attendee.t_shirt_size} originalAttendee={originalAttendee} isMobile={isMobile} />
      
      {/* Registration Date Cell */}
      <TableCell className={isMobile ? 'whitespace-nowrap pl-2 pr-1 py-2' : ''}>
        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
          <Calendar size={12} className="text-primary/60" />
          <span title={formatDateTime(attendee.registeredAt)} className="hover:text-primary cursor-help">
            {formatTimeAgo(attendee.registeredAt)}
          </span>
        </div>
      </TableCell>
      
      {/* Payment Status Cell */}
      <TableCell className={isMobile ? 'sticky right-[64px] bg-white/95 dark:bg-gray-900/95 shadow-[-4px_0px_8px_-2px_rgba(0,0,0,0.05)] z-10 pl-2 pr-1 py-2' : ''}>
        <PaymentToggle 
          attendee={attendee}
          isPaid={isPaid}
          isUpdating={isUpdating}
          onPaymentToggleRequest={handlePaymentToggleRequest}
        />
      </TableCell>
      
      {/* Attendance Status Cell */}
      <TableCell className={`${isMobile ? 'sticky right-0 bg-white/95 dark:bg-gray-900/95 shadow-[-4px_0px_8px_-2px_rgba(0,0,0,0.05)] z-10 pl-2 pr-1 py-2' : 'text-right'}`}>
        {isMobile ? (
          <div className="flex items-center justify-between gap-1">
            <AttendanceStatus 
              isAttended={localAttendance}
              checkedInAt={localCheckedInAt}
              formatDateTime={formatDateTime}
            />
            <CheckInButton 
              attendee={{
                ...attendee,
                attended: localAttendance,
                checkedInAt: localCheckedInAt
              }}
              onSuccess={onCheckInSuccess}
              onDirectUpdate={enhancedDirectUpdate} // Use enhanced version
              onSilentUpdate={onSilentUpdate}
            />
          </div>
        ) : (
          <AttendanceStatus 
            isAttended={localAttendance}
            checkedInAt={localCheckedInAt}
            formatDateTime={formatDateTime}
          />
        )}
      </TableCell>
      
      {/* Check-in Button Cell - only shown on desktop */}
      {!isMobile && (
        <TableCell className="text-center">
          <CheckInButton 
            attendee={{
              ...attendee,
              attended: localAttendance,
              checkedInAt: localCheckedInAt
            }}
            onSuccess={onCheckInSuccess}
            onDirectUpdate={enhancedDirectUpdate} // Use enhanced version
            onSilentUpdate={onSilentUpdate}
          />
        </TableCell>
      )}
      
      {/* Contribution Cell - moved to the end */}
      {showContribution && (
        <TableCell className={isMobile ? 'whitespace-nowrap pl-2 pr-1 py-2' : ''}>
          {attendee.contribution ? (
            <span className="text-blue-600 dark:text-blue-400 font-medium">
              {formatContribution(attendee.contribution)}
            </span>
          ) : (
            <span className="text-slate-400 dark:text-slate-500 text-sm italic">Chưa đóng góp</span>
          )}
        </TableCell>
      )}
    </>
  );
};
